import React, { useEffect, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiCloseCircleOutline } from "@mdi/js";
import {
  SpocModel,
  WarehouseModel,
} from "../../../../erp/inventory/models/warehouse_model";
import { TeamManagementOfWarehosues } from "../../../../erp/inventory/models/inventory_warehouse_spoc_model";
import {
  DeleteWarehouseManagerAPI,
  GetWarehosuesForEachMemberAPI,
} from "../warehouses/components/warehouse_spoc_mangement_apis";
import { AddMembersToWarehouse } from "./components/multi_warehouse_to_member_model";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../utils/enums";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

interface Props {
  warehouse?: WarehouseModel;
  warehouse_spocs?: SpocModel[];
}

export const TeamManagement: React.FC<Props> = () => {
  const { isAuthorised } = useAuth();
  const [search, setSearch] = useState("");
  const { showToast } = useToast();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [warehouseManager, setWarehouseManager] = useState(false);
  const [spocAssignments, setSpocAssignments] = useState<
    TeamManagementOfWarehosues[]
  >([]);
  const [editWarehouse, setEditWarehouse] = useState<
    TeamManagementOfWarehosues | undefined
  >(undefined);
  const [deleteWarehosue, setDeleteWarehouse] = useState<boolean>();
  const [refreshKey, setRefreshKey] = useState(0);
  const requestIdRef = React.useRef(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data when debounced search changes
  useEffect(() => {
    setCanManageTeam(
      isAuthorised({
        module: MODULE.warehouse_module_team_management,
        action: ACTION.UPDATE,
      }) ||
        isAuthorised({
          module: MODULE.warehouse_module_team_management,
          action: ACTION.CREATE,
        }),
    );
    const warehosue_manager = isAuthorised({
      module: MODULE.warehouse_manager,
      action: ACTION.CREATE,
    });

    setWarehouseManager(warehosue_manager);
  }, [isAuthorised]);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await GetWarehosuesForEachMemberAPI(debouncedSearch);
        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.log(
            `[TeamSearch] reqId=${currentRequestId} search="${debouncedSearch}" results=${res.data?.length ?? 0}`,
            res.data,
          );
          setSpocAssignments(res.data ?? []);
          setLoading(false);
        } else {
          console.log(
            `[TeamSearch] STALE reqId=${currentRequestId} (latest=${requestIdRef.current}) — dropped`,
          );
        }
      } catch (err) {
        console.error(err);
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [debouncedSearch, refreshKey]);

  const handleDeleteWarehouse = async (warehouseId: string, userId: string) => {
    if (!window.confirm("Are you sure you want to delete this mapping?"))
      return;
    try {
      setLoading(true);
      const res = await DeleteWarehouseManagerAPI({
        warehouse_id: warehouseId,
        uid: userId,
      });

      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message,
        });
        // 🔥 Update UI
        setSpocAssignments((prev) =>
          prev.map((spoc) => {
            // match correct member
            if (spoc.member.uid !== userId) return spoc;

            return {
              ...spoc,
              warehouses: spoc.warehouses.filter(
                (w: any) => w.warehouse_id !== warehouseId,
              ),
            };
          }),
        );
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wh-mobile-shrink space-y-4 p-5">
      {/* SEARCH + ADD */}
      <div className="flex font-bold text-3xl">
        <h2>Team Management</h2>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2">
          <input
            id="warehouse-team-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Team Members..."
            className="w-full border rounded-md px-3 py-1.5 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {search && (
          <button
            id="warehouse-team-search-clear-btn"
            onClick={() => setSearch("")}
            className="text-gray-400 hover:text-red-500 transition-colors"
            title="Clear search"
          >
            <Icon path={mdiCloseCircleOutline} size={0.7} />
          </button>
        )}
        {canManageTeam && (
          <button
            id="warehouse-team-add-member-btn"
            onClick={() => setOpenModal(true)}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
          >
            + Add Member
          </button>
        )}
      </div>

      {/* WAREHOUSE CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {spocAssignments?.map((item) => {
          const { member, warehouses, role } = item;

          const initials = member.contact_name
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();

          return (
            <div
              key={`${member.uid}-${role}`}
              id={`warehouse-team-member-card-${member.uid}-${role}`}
              className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition"
            >
              {/* HEADER */}
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                  {initials}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {member.contact_name}
                    </h3>

                    {/* Role Badge */}
                    <span className="text-xs px-3 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {role}
                    </span>
                  </div>

                  <p className="text-sm text-gray-500 truncate">
                    {member.contact_email}
                  </p>
                </div>
              </div>

              <hr className="my-4" />

              {/* ASSIGNED WAREHOUSES */}
              <div>
                <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                  🏬 Assigned Warehouses
                </p>

                <div className="flex flex-wrap gap-2">
                  {warehouses?.map((item: any) => (
                    <div
                      key={item.warehouse.id}
                      className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
                    >
                      <span
                        title={item.warehouse.name}
                        className="text-sm font-medium text-gray-700"
                      >
                        {item.warehouse.name}
                      </span>

                      {warehouseManager && role === "manager" && (
                        <button
                          id={`warehouse-team-delete-warehouse-btn-${item.warehouse.id}`}
                          onClick={() => {
                            handleDeleteWarehouse(
                              item.warehouse.id,
                              member.uid!,
                            );
                          }}
                          className="text-gray-500 hover:text-red-600 text-xs"
                          title="Delete"
                          disabled={!canManageTeam}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <hr className="my-4" />

              <div className="flex justify-end gap-4">
                {role !== "manager" && (
                  <button
                    id={`warehouse-team-edit-member-btn-${member.uid}-${role}`}
                    onClick={() => {
                      if (!canManageTeam) return;
                      setEditWarehouse(item);
                      setOpenModal(true);
                    }}
                    className="text-gray-500 hover:text-blue-600"
                    title="Edit"
                    disabled={!canManageTeam}
                  >
                    ✏️
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openModal && (
        <div
          id="warehouse-team-add-member-modal"
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          <div
            id="warehouse-team-add-member-modal-overlay"
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => {
              setOpenModal(false);
              setEditWarehouse(undefined);
            }}
          />

          {/* MODAL */}
          <div className="relative z-10 border">
            <AddMembersToWarehouse
              editData={editWarehouse}
              onClose={() => {
                setOpenModal(false);
                setEditWarehouse(undefined);
              }}
              onSaved={() => setRefreshKey((k) => k + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
